import React, { useRef, useState } from 'react';
import { Button, Modal, Tooltip } from 'antd';
import { DeleteOutlined, DownloadOutlined, EyeOutlined, FileImageOutlined, HistoryOutlined, TranslationOutlined } from '@ant-design/icons';

export type MetadataCoverDisplaySource = 'none' | 'original' | 'translated' | 'translated_placeholder_original';

interface MetadataCoverSectionProps {
  displayCoverUrl: string;
  indicatorLabel?: string | null;
  coverDisplaySource?: MetadataCoverDisplaySource;
  actionLabel: string;
  isCoverActionDisabled?: boolean;
  disabledActionReason?: string;
  showRemoveCover: boolean;
  setShowRemoveCover: React.Dispatch<React.SetStateAction<boolean>>;
  onDownloadCover: () => void;
  onRemoveCover: () => Promise<void> | void;
  onCoverUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenCoverGeneration: () => void;
  onOpenCoverHistory: () => void;
}

export const resolveMetadataCoverReadiness = (
  displayCoverUrl: string,
  coverDisplaySource: MetadataCoverDisplaySource = displayCoverUrl ? 'translated' : 'none',
) => {
  if (coverDisplaySource === 'translated_placeholder_original') {
    return { tone: 'attention', label: 'Source cover shown' };
  }
  if (displayCoverUrl) {
    return { tone: 'ready', label: 'Cover ready' };
  }
  return { tone: 'needed', label: 'Cover needed' };
};

const MetadataCoverSection: React.FC<MetadataCoverSectionProps> = ({
  displayCoverUrl,
  indicatorLabel,
  coverDisplaySource = displayCoverUrl ? 'translated' : 'none',
  actionLabel,
  isCoverActionDisabled = false,
  disabledActionReason,
  showRemoveCover,
  setShowRemoveCover,
  onDownloadCover,
  onRemoveCover,
  onCoverUpload,
  onOpenCoverGeneration,
  onOpenCoverHistory,
}) => {
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const isTranslateCoverAction = /translate/i.test(actionLabel);
  const normalizedCoverActionLabel = isTranslateCoverAction ? 'Translate cover' : 'Generate cover';
  const coverActionIcon = isTranslateCoverAction ? <TranslationOutlined /> : <FileImageOutlined />;

  const openUploadPicker = () => {
    uploadInputRef.current?.click();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    if (files.length === 0 || !uploadInputRef.current) {
      return;
    }
    uploadInputRef.current.files = files;
    const changeEvent = new Event('change', { bubbles: true });
    uploadInputRef.current.dispatchEvent(changeEvent);
  };

  const coverActionButton = (
    <Button
      icon={coverActionIcon}
      disabled={isCoverActionDisabled}
      onClick={onOpenCoverGeneration}
      aria-label={normalizedCoverActionLabel}
      data-testid="tutorial:cover:open-generation"
    >
      {normalizedCoverActionLabel}
    </Button>
  );
  const { tone: coverStatusTone, label: coverStatusLabel } = resolveMetadataCoverReadiness(
    displayCoverUrl,
    coverDisplaySource,
  );
  const coverUsageLabel = coverDisplaySource === 'translated'
    ? 'Localized cover'
    : coverDisplaySource === 'translated_placeholder_original'
      ? 'Source cover shown'
      : coverDisplaySource === 'original'
        ? 'Source cover'
        : 'Upload or generate a cover';

  return (
    <section
      id="metadata-cover"
      className="metadata-cover-section book-detail-secondary-surface"
      data-tour-id="metadata-cover"
      data-testid="tutorial:metadata:covers"
      tabIndex={-1}
    >
      <div className="metadata-section-heading book-detail-section-heading metadata-cover-section-heading">
        <div>
          <div className="metadata-section-title-row">
            <h3>Cover</h3>
            <span className={`metadata-readiness-pill metadata-readiness-pill--${coverStatusTone}`}>
              {coverStatusLabel}
            </span>
          </div>
          <p className="metadata-cover-section-helper">
            Store and export cover.
          </p>
        </div>
      </div>

      <div className="metadata-cover-content">
        <div className="metadata-cover-preview-column">
          {displayCoverUrl ? (
            <div
              className="metadata-cover-preview"
              onMouseEnter={() => setShowRemoveCover(true)}
              onMouseLeave={() => setShowRemoveCover(false)}
            >
              <img src={displayCoverUrl} alt="Cover" key={displayCoverUrl} />
              {showRemoveCover && (
                <div className="metadata-cover-overlay">
                  <Tooltip title="View larger">
                    <Button
                      icon={<EyeOutlined />}
                      onClick={() => setIsPreviewVisible(true)}
                      aria-label="View Cover Larger"
                    />
                  </Tooltip>
                  <Tooltip title="Download JPEG">
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={onDownloadCover}
                      aria-label="Download cover as JPEG"
                    />
                  </Tooltip>
                  <Tooltip title="Remove cover">
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={onRemoveCover}
                      aria-label="Remove Cover"
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          ) : (
            <div
              className="metadata-cover-upload"
              role="button"
              tabIndex={0}
              onClick={openUploadPicker}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openUploadPicker();
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onDrop={handleDrop}
            >
              <FileImageOutlined className="metadata-cover-upload-icon" />
              <span>Click or drag cover image</span>
            </div>
          )}
          <input
            ref={uploadInputRef}
            id="cover-upload"
            type="file"
            onChange={onCoverUpload}
            accept="image/*"
            style={{ display: 'none' }}
          />
        </div>
        <div className="metadata-cover-details">
          <div className="metadata-cover-label-row">
            <div className="metadata-cover-label">{indicatorLabel || 'No cover selected'}</div>
          </div>
          <p className="metadata-cover-helper">
            Used for store listings, exports, and translated editions.
          </p>
          <div className="metadata-cover-inline-actions">
            {displayCoverUrl ? (
              <>
                <Tooltip title="View cover">
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => setIsPreviewVisible(true)}
                    aria-label="View cover"
                  >
                    View cover
                  </Button>
                </Tooltip>
                <Tooltip title="Download JPEG">
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={onDownloadCover}
                    aria-label="Download cover as JPEG"
                  >
                    Download JPEG
                  </Button>
                </Tooltip>
              </>
            ) : null}
            {isCoverActionDisabled && disabledActionReason ? (
              <Tooltip title={disabledActionReason}>
                <span>{coverActionButton}</span>
              </Tooltip>
            ) : (
              <Tooltip title={normalizedCoverActionLabel}>
                {coverActionButton}
              </Tooltip>
            )}
            <Tooltip title="Cover history">
                 <Button
                   icon={<HistoryOutlined />}
                   data-tour-id="metadata-cover-history"
                   data-testid="tutorial:cover:history"
                   onClick={onOpenCoverHistory}
                aria-label="Cover history"
              >
                Cover history
               </Button>
             </Tooltip>
          </div>
          <div className="metadata-cover-note">
            <span className={`metadata-cover-note-dot metadata-cover-note-dot--${coverStatusTone}`} />
            <span>{coverUsageLabel}</span>
          </div>
        </div>
      </div>
      {displayCoverUrl ? (
        <Modal
          visible={isPreviewVisible}
          title={
            <div className="metadata-cover-preview-titlebar">
              <span className="metadata-cover-preview-title">
                {indicatorLabel || 'Cover preview'}
              </span>
            </div>
          }
          footer={(
            <div className="metadata-cover-preview-footer metadata-cover-preview-footer--actions-only">
              <div className="metadata-cover-preview-actions">
                {isCoverActionDisabled && disabledActionReason ? (
                  <Tooltip title={disabledActionReason}>
                    <span>{coverActionButton}</span>
                  </Tooltip>
                ) : coverActionButton}
                <Button
                  icon={<HistoryOutlined />}
                  onClick={onOpenCoverHistory}
                >
                  Cover history
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={onDownloadCover}
                >
                  Download JPEG
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={onRemoveCover}
                >
                  Delete cover
                </Button>
              </div>
            </div>
          )}
          onCancel={() => setIsPreviewVisible(false)}
          centered
          width="min(760px, calc(100vw - 32px))"
          className="metadata-cover-preview-modal"
        >
          <div className="metadata-cover-preview-body">
            <div className="metadata-cover-preview-stage">
              <img src={displayCoverUrl} alt={indicatorLabel || 'Cover preview'} className="metadata-cover-preview-large" />
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
};

export default MetadataCoverSection;
